// Tiptap 文章编辑器(D3)。
//
// StarterKit(段落/标题/列表/加粗/斜体等)+ Link(http/https 白名单,唯一存 url 的节点)
// + fileRef / packRef 自定义节点(只存 uuid)。工具栏含格式按钮 + 插入文件/包/链接入口。
//
// 嵌入节点在编辑器内用 ReactNodeViewRenderer 渲染占位卡(实际元数据解析在详情渲染层
// article-render.tsx,按 uuid 运行时解析)。

import { useEffect, type ReactNode } from 'react'
import {
  EditorContent,
  useEditor,
  ReactNodeViewRenderer,
  NodeViewWrapper,
  type Editor,
  type NodeViewProps,
} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import type { JSONContent } from '@tiptap/core'
import { cn } from '@dogdisk/shared'
import {
  Bold,
  Heading2,
  Heading3,
  Italic,
  Link2,
  List,
  ListOrdered,
  Package,
  Paperclip,
  Quote,
} from 'lucide-react'
import { FileRefNode, type FileRefAttrs } from './nodes/file-ref'
import { PackRefNode, type PackRefAttrs } from './nodes/pack-ref'
import { useMyFileName, useMyPackName } from './use-embed-name'

// 编辑器内嵌入占位卡:节点只存 uuid,名字按 id 运行时解析(v_*_mine);
// 解析不到(加载中/已删)回退「嵌入文件/嵌入资源包」。真元数据在详情页渲染。
function FileRefPlaceholder({ node }: NodeViewProps) {
  const { fileId } = node.attrs as FileRefAttrs
  const { data: name } = useMyFileName(fileId)
  return (
    <NodeViewWrapper
      data-testid="article-editor-fileref"
      className="my-2 flex items-center gap-2 rounded-lg border border-dashed border-[#19caad] bg-[#ebfaf6] px-3 py-2 text-[12px] text-[#0f7969]"
    >
      <svg className="shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2Z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
      <span className="min-w-0 truncate">{name ?? '嵌入文件'}</span>
    </NodeViewWrapper>
  )
}

function PackRefPlaceholder({ node }: NodeViewProps) {
  const { packId } = node.attrs as PackRefAttrs
  const { data: name } = useMyPackName(packId)
  return (
    <NodeViewWrapper
      data-testid="article-editor-packref"
      className="my-2 flex items-center gap-2 rounded-lg border border-dashed border-[#2563EB] bg-[#eff6ff] px-3 py-2 text-[12px] text-[#1d4ed8]"
    >
      <svg className="shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 8v13H3V8M1 3h22v5H1zM10 12h4" />
      </svg>
      <span className="min-w-0 truncate">{name ?? '嵌入资源包'}</span>
    </NodeViewWrapper>
  )
}

const FileRefWithView = FileRefNode.extend({
  addNodeView() {
    return ReactNodeViewRenderer(FileRefPlaceholder)
  },
})

const PackRefWithView = PackRefNode.extend({
  addNodeView() {
    return ReactNodeViewRenderer(PackRefPlaceholder)
  },
})

export const ARTICLE_EXTENSIONS = [
  StarterKit.configure({ link: false }),
  Link.configure({
    openOnClick: false,
    autolink: true,
    protocols: ['http', 'https'],
    HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
  }),
  FileRefWithView,
  PackRefWithView,
]

export type ArticleEditorHandle = Editor

export function useArticleEditor(args: {
  content?: JSONContent | null
  onUpdate?: (json: JSONContent) => void
}): Editor | null {
  const { content, onUpdate } = args
  const editor = useEditor({
    extensions: ARTICLE_EXTENSIONS,
    content: content ?? '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none min-h-[200px] focus:outline-none',
        'data-testid': 'article-editor-content',
      },
    },
    onUpdate: ({ editor }) => onUpdate?.(editor.getJSON()),
  })
  return editor
}

type ArticleEditorProps = {
  editor: Editor | null
  /** 打开「插入文件」面板 */
  onInsertFile?: () => void
  /** 打开「插入资源包」面板 */
  onInsertPack?: () => void
}

/** 文章编辑器(工具栏 + 内容区)。content 状态由父组件经 useArticleEditor 持有。 */
export function ArticleEditor({ editor, onInsertFile, onInsertPack }: ArticleEditorProps) {
  // 卸载时销毁 editor 由 useEditor 自动处理;此处仅保证 SSR-safe。
  useEffect(() => undefined, [editor])

  if (!editor) {
    return (
      <div className="flex h-[200px] items-center justify-center text-[13px] text-[#9CA3AF]">
        加载编辑器…
      </div>
    )
  }

  const setLink = () => {
    const prev = (editor.getAttributes('link') as { href?: string }).href ?? ''
    const url = window.prompt('链接地址(http/https)', prev)
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    if (!/^https?:\/\//i.test(url)) {
      window.alert('仅支持 http / https 链接')
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="article-editor">
      {/* Toolbar(jRwg9)— FormatRow 图标按钮 + InsertRow 文件/资源包胶囊,#FAFAFA 条 */}
      <div className="flex flex-col gap-2 border-y border-[#F3F4F6] bg-[#FAFAFA] px-3 py-2">
        <div className="flex items-center gap-1">
          <IconBtn label="加粗" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={16} /></IconBtn>
          <IconBtn label="斜体" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={16} /></IconBtn>
          <IconBtn label="二级标题" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={16} /></IconBtn>
          <IconBtn label="三级标题" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={16} /></IconBtn>
          <IconBtn label="无序列表" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={16} /></IconBtn>
          <IconBtn label="有序列表" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={16} /></IconBtn>
          <IconBtn label="引用" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={16} /></IconBtn>
          <IconBtn label="链接" active={editor.isActive('link')} onClick={setLink}><Link2 size={16} /></IconBtn>
        </div>
        <div className="flex items-center gap-2">
          <InsertPill testid="article-editor-insert-file" onClick={() => onInsertFile?.()}>
            <Paperclip size={14} />
            文件
          </InsertPill>
          <InsertPill testid="article-editor-insert-pack" onClick={() => onInsertPack?.()}>
            <Package size={14} />
            资源包
          </InsertPill>
        </div>
      </div>
      {/* EditorArea(VpBIi)— 滚动正文区 */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}

/** 工具栏图标按钮(32×32;选中态品牌色填充)。 */
function IconBtn({
  active,
  onClick,
  label,
  children,
}: {
  active?: boolean
  onClick: () => void
  label: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-md transition-colors',
        active ? 'bg-[#19caad] text-white' : 'text-[#374151] active:bg-[#EDEEF0]',
      )}
    >
      {children}
    </button>
  )
}

/** 插入胶囊(文件 / 资源包;#F0FBF9 浅底品牌字)。 */
function InsertPill({
  testid,
  onClick,
  children,
}: {
  testid?: string
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="flex items-center gap-1 rounded-md bg-[#F0FBF9] px-2 py-1.5 text-[11px] font-semibold text-[#19caad] active:bg-[#d3f4ec]"
    >
      {children}
    </button>
  )
}
